import CopyAllOutlinedIcon from "@mui/icons-material/CopyAllOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import HelpOutlineIcon from "@mui/icons-material/Help";
import { Tooltip, useTheme, Zoom } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import type React from "react";
import type { ComponentProps } from "react";
import { Link } from "react-router-dom";

import { CredentialStatus } from "../../credentialStatus";
import type { ActionItem, CellParams, DataTableColDef } from "../../DataTable";
import { redactText, renderCellRedacted } from "../../redact";
import { CREDENTIAL_TYPE, isSupportPayer, typeFromCredential } from "../common";
import { EmailMfaCode } from "../ui/EmailMfaCode";
import { MaskValue } from "../ui/Mask";
import { InvalidChip, NotWorkingChip, WorkingChip } from "../ui/StateChips";
import { TotpCode } from "../ui/TotpCode";

export type CredentialRow = Partial<UserCredentialsMessage> & {
  id?: string;
  practices: PracticeMessage[];
  unmaskValue: (attributeName: string) => Promise<string>;
  fetchTotp: () => Promise<TotpMessage>;
  fetchEmailMfa: ComponentProps<typeof EmailMfaCode>["fetchCode"];
};

type CredentialParams = CellParams<CredentialRow>;

function CredentialWebsiteLink({
  website,
  children,
}: {
  website: string;
  children: React.ReactNode;
}) {
  const theme = useTheme();
  return (
    <Link
      style={{
        textDecoration: "underline",
        color: theme.palette.primary.main,
      }}
      target="_blank"
      rel="noopener noreferrer"
      to={website}
    >
      {children}
    </Link>
  );
}

export function getDefaultColumns(
  practiceNames: string[],
  credentialNames: string[],
  supportedSourcesMap: Record<string, SourceMessage>,
  openEdit: (row: CredentialRow) => Promise<void>,
  openDelete: (row: CredentialRow) => void,
  openToggleStatus: (row: CredentialRow) => void,
  devMode = false,
  showTypeColumn = true,
  openDuplicate?: (row: CredentialRow) => Promise<void>,
): DataTableColDef<CredentialRow>[] {
  const columns: DataTableColDef<CredentialRow>[] = [
    ...(showTypeColumn
      ? [
          {
            field: "type",
            headerName: "Type",
            type: "singleSelect",
            valueOptions: Object.values(CREDENTIAL_TYPE),
            valueGetter: (_value: unknown, row: CredentialRow) =>
              typeFromCredential(row),
            flex: 1,
          } as DataTableColDef<CredentialRow>,
        ]
      : []),
    {
      field: "practiceId",
      headerName: "Practice",
      type: "singleSelect",
      valueOptions: practiceNames,
      valueGetter: (value: unknown, row: CredentialRow) =>
        row.practices.find((practice) => practice.wieldyId === value)
          ?.displayName,
      renderCell: ({ value }: CredentialParams) => (
        <div data-sr-redact>{redactText(value as string | undefined)}</div>
      ),
      flex: 1,
    },
    {
      field: "sourceName",
      headerName: "Credential",
      type: "singleSelect",
      valueOptions: credentialNames,
      valueGetter: (value: unknown, row: CredentialRow) =>
        row.name || row.pmsDisplayName || value,
      flex: 1,
      renderCell: (props: CredentialParams) => {
        const credential = props.row;
        if (credential.pmsDisplayName) {
          return <div>{credential.pmsDisplayName}</div>;
        }
        const website =
          credential.website ||
          supportedSourcesMap[credential.sourceId ?? ""]?.website;
        if (website) {
          return (
            <CredentialWebsiteLink website={website}>
              <div>{props.value as string}</div>
            </CredentialWebsiteLink>
          );
        }
        return <div>{props.value as string}</div>;
      },
    },
    {
      field: "username",
      headerName: "Username",
      flex: 1,
      renderCell: (props: CredentialParams) => (
        <MaskValue unmaskValue={async () => props.row.unmaskValue("username")}>
          {renderCellRedacted(props)}
        </MaskValue>
      ),
    },
    {
      field: "password",
      headerName: "Password",
      flex: 1.25,
      sortable: false,
      renderCell: (props: CredentialParams) => (
        <MaskValue unmaskValue={async () => props.row.unmaskValue("password")}>
          {renderCellRedacted(props)}
        </MaskValue>
      ),
    },
    ...(devMode
      ? [
          {
            field: "mfaJson",
            headerName: "MFA Config",
            flex: 1,
            sortable: false,
            renderCell: (props: CredentialParams) => {
              if (!props.row.sourceId) {
                return <div>—</div>;
              }
              return (
                <MaskValue
                  unmaskValue={async () => props.row.unmaskValue("mfa_json")}
                >
                  {renderCellRedacted(props)}
                </MaskValue>
              );
            },
          } as DataTableColDef<CredentialRow>,
          {
            field: "totp",
            headerName: "MFA Code",
            flex: 1,
            sortable: false,
            filterable: false,
            renderCell: (props: CredentialParams) => {
              if (!props.row.sourceId) {
                return <div>—</div>;
              }
              if (props.row.mfaType === "AUTHENTICATOR") {
                return <TotpCode fetchCode={() => props.row.fetchTotp()} />;
              }
              if (
                props.row.mfaType === "EMAIL" &&
                props.row.emailMfaSupported
              ) {
                return (
                  <EmailMfaCode fetchCode={() => props.row.fetchEmailMfa()} />
                );
              }
              return <div>—</div>;
            },
          } as DataTableColDef<CredentialRow>,
        ]
      : []),
    {
      field: "notes",
      headerName: "Notes",
      flex: 0.5,
      sortable: false,
      renderCell: ({ value }: CredentialParams) =>
        value ? (
          <div>
            <IconButton
              sx={{ color: "#475467", padding: "5px" }}
              onClick={() => {
                if (value) {
                  window.navigator.clipboard.writeText(value as string);
                }
              }}
            >
              <CopyAllOutlinedIcon sx={{ width: "20px", height: "20px" }} />
            </IconButton>
            <Tooltip
              title={
                <div style={{ whiteSpace: "pre-line" }}>{value as string}</div>
              }
              TransitionComponent={Zoom}
              placement="top"
              arrow
            >
              <IconButton
                aria-label="tooltip notes"
                size="small"
                sx={{ color: "#475467", padding: "5px" }}
              >
                <HelpOutlineIcon sx={{ width: "20px", height: "20px" }} />
              </IconButton>
            </Tooltip>
          </div>
        ) : (
          "-"
        ),
    },
    {
      field: "userId",
      headerName: "User ID",
      flex: 0.75,
      sortable: true,
      renderCell: ({ value }: CredentialParams) => (
        <div>{(value as string | undefined) ?? "—"}</div>
      ),
    } as DataTableColDef<CredentialRow>,
    {
      field: "status",
      headerName: "Status",
      type: "singleSelect",
      valueOptions: Object.keys(CredentialStatus),
      flex: 1,
      renderCell: ({ value }: CredentialParams) => {
        if (value === CredentialStatus.INACTIVE) {
          return <NotWorkingChip />;
        }
        if (value === CredentialStatus.INVALID) {
          return <InvalidChip />;
        }
        return <WorkingChip />;
      },
    },
    {
      field: "settings",
      type: "actions",
      getActions: ({ row }) => {
        const actions: ActionItem[] = [
          { label: "Edit", icon: <EditIcon />, onClick: () => openEdit(row) },
        ];

        if (openDuplicate && isSupportPayer(row)) {
          actions.push({
            label: "Duplicate",
            icon: <CopyAllOutlinedIcon />,
            onClick: () => openDuplicate(row),
          });
        }

        if (
          devMode &&
          (row.status === CredentialStatus.ACTIVE ||
            row.status === CredentialStatus.INACTIVE)
        ) {
          actions.push({
            label:
              row.status === CredentialStatus.ACTIVE
                ? "Set Inactive"
                : "Set Active",
            icon: <EditIcon />,
            onClick: () => openToggleStatus(row),
          });
        }

        actions.push({
          label: "Delete",
          icon: <DeleteIcon />,
          onClick: () => openDelete(row),
        });

        return actions;
      },
    },
  ];

  return columns;
}
